import { ChangeEventWorkflow, SaveChangeEventConfigChangeInput } from "src/generated/graphqlTypes";
import {
  baseChangeEventFormConfig,
  BaseChangeEventFormInput,
  baseChangeEventMapToForm,
} from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import { OptionPriceMode } from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import { VersionUpdateStep } from "src/routes/projects/change-events/guided/workflows/configurationTrueUp/1.VersionUpdateStep";
import { DiscontinuedOptionsStep } from "src/routes/projects/change-events/guided/workflows/configurationTrueUp/2.DiscontinuedOptionsStep";
import { DiscontinuedOptionsPane } from "src/routes/projects/change-events/guided/workflows/configurationTrueUp/components/DiscontinuedOptionsPane";
import {
  reconcileImpactedCommitmentsFormConfig,
  ReconcileImpactedCommitmentsFormInput,
  reconcileImpactedCommitmentsMapToForm,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/ReconcileImpactedCommitmentsStep";
import { ReviewImpactStep } from "src/routes/projects/change-events/guided/workflows/sharedSteps/ReviewImpactStep";
import {
  reconcileImpactedCommitmentsStep,
  reviewStep,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/sharedSteps";
import { useGuidedChangeEventActions } from "src/routes/projects/change-events/guided/workflows/sharedSteps/useGuidedChangeEventActions";
import { useMaybeOpenCommitmentCleanupModal } from "src/routes/projects/change-events/guided/workflows/sharedSteps/useMaybeOpenCommitmentCleanupModal";
import { useReviewStepActions } from "src/routes/projects/change-events/guided/workflows/sharedSteps/useReviewStepActions";
import { defineWorkflow } from "src/routes/projects/change-events/guided/workflows/types";
import { idsOf } from "src/utils";

export type ConfigurationTrueUpFormInput = BaseChangeEventFormInput & {
  // The Discontinued Options step saves the selected options onto the Draft `result` via `saveCheckoutConfig`, and
  // reconciling the change event is deferred until the user advances past it.
  configChange?:
    | (Pick<SaveChangeEventConfigChangeInput, "id"> & {
        // Local-only fields:
        /** The Draft `configChange.result` config the selected options are saved onto. */
        resultId?: string | null;
        /** The `result` options returned by the last `saveCheckoutConfig`. */
        optionIds?: string[] | null;
        /** The discontinued options the user replaced or removed instead of keeping; empty means all are kept. */
        droppedOptionIds?: string[] | null;
      })
    | null;
  // Local-only field to track the price mode selected by the user (cost or price).
  priceMode?: OptionPriceMode;
} & ReconcileImpactedCommitmentsFormInput;

export const configurationTrueUpWorkflow = defineWorkflow<ConfigurationTrueUpFormInput>({
  workflow: ChangeEventWorkflow.ConfigurationTrueUp,
  formConfig: {
    ...baseChangeEventFormConfig,
    priceMode: { type: "value", isLocalOnly: true },
    configChange: {
      type: "object",
      config: {
        id: { type: "value" },
        resultId: { type: "value", isLocalOnly: true },
        optionIds: { type: "value", isLocalOnly: true },
        droppedOptionIds: { type: "value", isLocalOnly: true },
      },
    },
    ...reconcileImpactedCommitmentsFormConfig,
  },
  // Create change event upfront since this workflow doesn't have a user selected target config
  // and instead it's generated on the backend from the latest promoted Product Offering version.
  beforeStart: async ({ projectId: structureId, input, save }) => {
    const result = await save({ variables: { structureId, input: { ...input, configChange: {} } } });
    return { changeEventId: result.data!.saveChangeEvent.changeEvent.id };
  },
  mapToForm: (changeEvent) => {
    const { configChange } = changeEvent;
    return {
      ...baseChangeEventMapToForm(changeEvent),
      priceMode: OptionPriceMode.Price,
      configChange: {
        id: configChange?.id,
        resultId: configChange?.result?.id,
        optionIds: idsOf(configChange?.result?.config.options ?? [], "readyPlanOption"),
        droppedOptionIds: null,
      },
      ...reconcileImpactedCommitmentsMapToForm(changeEvent),
    };
  },
  steps: [
    {
      value: GuidedChangeEventStep.VersionUpdate,
      label: "Version Update",
      isComplete: () => true,
      render: (form) => <VersionUpdateStep formState={form} />,
    },
    {
      value: GuidedChangeEventStep.DiscontinuedOptions,
      label: "Discontinued Options",
      isComplete: () => true,
      render: (form) => <DiscontinuedOptionsStep formState={form} />,
      rightPaneTriggers: (form) => [
        { icon: "list", label: "Changes", content: <DiscontinuedOptionsPane formState={form} /> },
      ],
    },
    {
      value: GuidedChangeEventStep.ReviewImpact,
      label: "Review Impact",
      isComplete: () => true,
      render: (form) => <ReviewImpactStep formState={form} />,
    },
    reconcileImpactedCommitmentsStep(),
    reviewStep(),
  ],
  useActions: (args) => {
    const { saveChangeEvent } = useGuidedChangeEventActions(args, (form) => ({
      configChange: { id: form.configChange.id.value },
    }));
    const { submit } = useReviewStepActions(args);
    const save = (opts: { andExit?: boolean } = {}) =>
      // The selected options are already saved on `result` via `saveCheckoutConfig`, so the form might not be dirty.
      // Force the save so the change event is still re-derived from them
      saveChangeEvent({ ...opts, force: !!args.form.needsReconcile.value });
    const saveAndExit = () => save({ andExit: true });
    // Replacing or removing a discontinued option can rewrite scope that's already on a purchase order, so submitting
    // is gated on those being cleaned up by hand first.
    const { submitWithCommitmentCleanup } = useMaybeOpenCommitmentCleanupModal({
      form: args.form,
      onSubmit: submit,
      onSaveDraftAndExit: saveAndExit,
    });
    return {
      onSubmit: submitWithCommitmentCleanup,
      onContinue: { [GuidedChangeEventStep.DiscontinuedOptions]: () => save() },
      onSaveAndExit: saveAndExit,
    };
  },
});
